import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { fetchWithAuth } from "helpers/api-with-auth";
import { Loading } from "components/loading";
import {
  Container,
  Header,
  Title,
  Subtitle,
  ErrorMessage,
  ActionButtons,
  PrimaryButton,
  SecondaryButton,
} from "./styles";

// Map OAuth error codes to user-friendly messages
function getErrorMessage(errorCode: string, errorDescription: string | null): {
  title: string;
  message: string;
  canRetry: boolean;
} {
  switch (errorCode) {
    case "access_denied":
      return {
        title: "Access Denied",
        message: "You declined to share your health records. You can still complete your assessment manually.",
        canRetry: true,
      };
    case "invalid_scope":
      return {
        title: "Connection Not Available",
        message: "This healthcare provider's connection is not fully configured yet. Please try again later or enter your information manually.",
        canRetry: false,
      };
    case "invalid_request":
      return {
        title: "Connection Error",
        message: errorDescription || "There was a problem with the connection request. Please try again.",
        canRetry: true,
      };
    case "unauthorized_client":
      return {
        title: "Authorization Error",
        message: "This application is not authorized to connect to your healthcare provider. Please contact support.",
        canRetry: false,
      };
    case "server_error":
      return {
        title: "Provider Error",
        message: "Your healthcare provider's system encountered an error. Please try again later.",
        canRetry: true,
      };
    case "temporarily_unavailable":
      return {
        title: "Temporarily Unavailable",
        message: "Your healthcare provider's system is temporarily unavailable. Please try again in a few minutes.",
        canRetry: true,
      };
    default:
      return {
        title: "Connection Failed",
        message: errorDescription || "Unable to connect to your healthcare provider. Please try again.",
        canRetry: true,
      };
  }
}

interface ErrorState {
  title: string;
  message: string;
  canRetry: boolean;
}

export function EhrCallbackPage() {
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState<ErrorState | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  const handleSkip = useCallback(() => {
    // Clean up and mark as skipped
    sessionStorage.removeItem("ehr_oauth_state");
    sessionStorage.setItem("ehr_skipped", "true");
    sessionStorage.setItem("ehr_flow_complete", "true");
    history.push("/");
  }, [history]);

  const handleRetry = useCallback(() => {
    sessionStorage.removeItem("ehr_oauth_state");
    history.push("/ehr-connect");
  }, [history]);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");
      const errorParam = params.get("error");

      // Check for OAuth error from provider
      if (errorParam) {
        const errorDescription = params.get("error_description");
        const errorInfo = getErrorMessage(errorParam, errorDescription);
        setError(errorInfo);
        setIsProcessing(false);
        return;
      }

      // Verify state matches what we stored
      const storedState = sessionStorage.getItem("ehr_oauth_state");
      if (state !== storedState) {
        setError({
          title: "Security Error",
          message: "The connection request could not be verified. Please try again.",
          canRetry: true,
        });
        setIsProcessing(false);
        return;
      }

      // Exchange code for tokens
      try {
        const response = await fetchWithAuth(
          `${SPECIALIST_HOSTNAME}/api/v1/ehr_connections/callback`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ code, state }),
          }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "Failed to complete connection");
        }

        // Clean up session storage
        sessionStorage.removeItem("ehr_oauth_state");

        // Navigate to import status page
        history.push("/ehr-importing");
      } catch (err) {
        setError({
          title: "Connection Failed",
          message: err instanceof Error ? err.message : "Unable to complete the connection. Please try again.",
          canRetry: true,
        });
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [location, history]);

  if (error) {
    return (
      <Container>
        <Header>
          <Title>{error.title}</Title>
          <Subtitle>{error.message}</Subtitle>
        </Header>

        <ErrorMessage style={{ marginTop: 24 }}>
          We were unable to connect to your health records.
        </ErrorMessage>

        <ActionButtons>
          {error.canRetry && (
            <PrimaryButton onClick={handleRetry}>
              Try Again
            </PrimaryButton>
          )}
          <SecondaryButton onClick={handleSkip}>
            Skip & Enter Manually
          </SecondaryButton>
        </ActionButtons>
      </Container>
    );
  }

  if (isProcessing) {
    return (
      <Container>
        <Loading />
        <Title style={{ marginTop: 16 }}>Completing connection...</Title>
        <Subtitle>Please wait while we securely connect to your health records.</Subtitle>
      </Container>
    );
  }

  return null;
}

export default EhrCallbackPage;
